import React, { useRef, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ReactComponent as AddIcon } from "assets/images/icons/btn_add.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { OutlineButton } from "components/buttons/OutlineButton";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import { SolidButton } from "components/buttons/SolidButton";

const isValidIPv4 = (value) => {
  const octets = value.split(".");
  return octets.length === 4 && octets.every((octet) => {
    if (!/^\d{1,3}$/.test(octet)) return false;
    const number = Number(octet);
    return number >= 0 && number <= 255;
  });
};

const parseCsvLine = (line) => {
  const values = [];
  let value = "";
  let quoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (character === '"' && line[index + 1] === '"' && quoted) {
      value += '"';
      index += 1;
    } else if (character === '"') {
      quoted = !quoted;
    } else if (character === "," && !quoted) {
      values.push(value.trim());
      value = "";
    } else {
      value += character;
    }
  }
  values.push(value.trim());
  return values;
};

const parseDeviceCsv = (content) => {
  const lines = String(content || "").split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const rows = lines.filter((line) => !line.toLowerCase().startsWith("ip address"));
  const devices = rows
    .map((line) => {
      const [ipAddress = "", username = "", password = "", role = ""] = parseCsvLine(line);
      return { ipAddress, username, password, role: role || "Spine" };
    })
    .filter((device) => isValidIPv4(device.ipAddress));

  return { devices, totalCount: rows.length };
};

const ImportView = ({ form, onClose, onImport }) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);

  const handleImport = () => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const { devices, totalCount } = parseDeviceCsv(reader.result);
      onImport(devices, totalCount);
    };
    reader.readAsText(file);
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
            paddingLeft: "20px",
          }}
        >
          Select Devices File
        </Typography>
        <Button
          onClick={onClose}
          sx={{
            minWidth: 36,
            p: 0,
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": { backgroundColor: theme.palette.primary.transparent },
          }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "auto minmax(180px, 1fr) auto", alignItems: "center", width: "80%", gap: 2, mt: 4 }}>
          <Box sx={form.getLabelStyle()}>Choose File:</Box>
          <TextField
            value={file?.name || ""}
            placeholder="Select a CSV or TXT file"
            disabled
            sx={form.getTextfieldStyle(true)}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            onChange={(event) => setFile(event.target.files?.[0] || null)}
            style={{ display: "none" }}
          />
          <OutlineRectangleButton startIcon={<AddIcon />} height="32px" onClick={() => fileInputRef.current?.click()}>
            Browse
          </OutlineRectangleButton>
        </Box>
      </Box>

      <Box sx={{ margin: "24px 0 16px auto", display: "flex", justifyContent: "center" }}>
        <OutlineButton onClick={onClose} sx={{ marginRight: "20px" }} width="100px" height="32px">
          Cancel
        </OutlineButton>
        <SolidButton onClick={handleImport} width="100px" height="32px" disabled={!file}>
          Upload
        </SolidButton>
      </Box>
    </>
  );
};

export default React.memo(ImportView);
